import { WHITEBOARD_BASE_SIZE } from '@/lib/constants'

const defaultState = () => {
  return {
    context: null,
    isDrawing: false,
    tool: 'none',
    color: '#FFFFFF',
    size: 'md',
    oldX: 0,
    oldY: 0,
    newX: 0,
    newY: 0,
    remote: {
      oldX: 0,
      oldY: 0,
      newX: 0,
      newY: 0
    },
    magnification: {
      x: 1,
      y: 1
    },
    snapshotImage: null,
    sendData: null,
    sendImageData: null,
    receivedData: null
  }
}

const state = defaultState()

const getters = {
  context: state => state.context,
  isDrawing: state => state.isDrawing,
  tool: state => state.tool,
  color: state => state.color,
  size: state => state.size,
  position: state => {
    return {
      oldX: state.oldX,
      oldY: state.oldY,
      newX: state.newX,
      newY: state.newY,
      remote: {
        oldX: state.remote.oldX,
        oldY: state.remote.oldY,
        newX: state.remote.newX,
        newY: state.remote.newY
      }
    }
  },
  snapshotImage: state => state.snapshotImage,
  sendImageData: state => state.sendImageData,
  sendData: state => state.sendData,
  receivedData: state => state.receivedData
}

const mutations = {
  setContext(state, context) {
    context.strokeStyle = state.color
    context.lineWidth = 2
    state.context = context
  },
  setMagnification(state, payload) {
    const { width, height } = payload
    state.magnification.x = WHITEBOARD_BASE_SIZE.width / width
    state.magnification.y = WHITEBOARD_BASE_SIZE.height / height
  },
  setDrawing(state, payload) {
    const { isDrawing, x, y } = payload
    if (isDrawing) {
      state.oldX = state.newX = x * state.magnification.x
      state.oldY = state.newY = y * state.magnification.y
    }
    state.isDrawing = isDrawing
  },
  setRemoteDrawing(state, payload) {
    const { oldX, oldY, newX, newY, strokeStyle } = payload
    state.remote.oldX = oldX
    state.remote.oldY = oldY
    state.remote.newX = newX
    state.remote.newY = newY
    state.context.strokeStyle = strokeStyle
  },
  setTool(state, tool) {
    state.tool = tool
  },
  setColor(state, color) {
    state.color = color
    state.context.strokeStyle = color
  },
  toggleSize(state) {
    switch (state.size) {
      case 'sm': {
        state.size = 'md'
        state.context.lineWidth = 2
        break
      }
      case 'md': {
        state.size = 'lg'
        state.context.lineWidth = 4
        break
      }
      case 'lg': {
        state.size = 'sm'
        state.context.lineWidth = 1
        break
      }
    }
  },
  configureLineWidth(state, remoteSize) {
    const size = remoteSize ? remoteSize : state.size
    switch (size) {
      case 'sm': {
        state.context.lineWidth = 1
        break
      }
      case 'md': {
        state.context.lineWidth = 2
        break
      }
      case 'lg': {
        state.context.lineWidth = 4
        break
      }
    }
  },
  setNewPosition(state, payload) {
    const { x, y } = payload
    state.newX = x * state.magnification.x
    state.newY = y * state.magnification.y
  },
  finishToolAction(state) {
    state.oldX = state.newX
    state.oldY = state.newY
  },
  setSnapshotImage(state, payload) {
    state.snapshotImage = payload
  },
  setSendData(state, data) {
    state.sendData = JSON.stringify(data)
  },
  // NOTE: 一旦クローズ
  // setSendImageData(state) {
  //   state.sendImageData = new Uint8Array(
  //     state.context.getImageData(
  //       0,
  //       0,
  //       WHITEBOARD_BASE_SIZE.width,
  //       WHITEBOARD_BASE_SIZE.height
  //     ).data
  //   )
  //   state.sendImageData = state.context.getImageData(
  //     0,
  //     0,
  //     WHITEBOARD_BASE_SIZE.width,
  //     WHITEBOARD_BASE_SIZE.height
  //   ).data.buffer
  // },
  setReceivedData(state, data) {
    state.receivedData = data
  },
  resetModule(state) {
    Object.assign(state, defaultState())
  }
}

const actions = {
  toolAction: function({ getters, commit, dispatch }) {
    const { tool, color, size, position } = getters

    switch (tool) {
      case 'pencil': {
        dispatch('drawLine')
        commit('setSendData', {
          action: 'drawLine',
          strokeStyle: color,
          strokeSize: size,
          oldX: position.oldX,
          oldY: position.oldY,
          newX: position.newX,
          newY: position.newY
        })
        break
      }
      case 'eraser': {
        dispatch('eraseRect')
        commit('setSendData', {
          action: 'eraseRect',
          oldX: position.oldX,
          oldY: position.oldY,
          newX: position.newX,
          newY: position.newY
        })
        break
      }
    }
    commit('finishToolAction')
  },
  remoteAction: function({ commit, dispatch, getters }, payload) {
    const { action } = payload
    const { context, color } = getters

    commit('setRemoteDrawing', payload)
    commit('configureLineWidth', payload.strokeSize)

    switch (action) {
      case 'drawLine': {
        dispatch('drawLine', true)
        break
      }
      case 'eraseRect': {
        dispatch('eraseRect', true)
        break
      }
      case 'clear': {
        dispatch('clear')
        break
      }
    }

    commit('configureLineWidth')
    context.strokeStyle = color
    commit('finishToolAction')
  },
  drawLine: function({ getters }, remote = false) {
    const { context, position } = getters
    context.beginPath()
    context.moveTo(
      remote ? position.remote.oldX : position.oldX,
      remote ? position.remote.oldY : position.oldY,
    )
    context.lineTo(
      remote ? position.remote.newX : position.newX,
      remote ? position.remote.newY : position.newY,
    )
    context.stroke()
    context.closePath()
  },
  eraseRect: function({ getters, commit, dispatch }, remote = false) {
    const { context } = getters
    const { strokeStyle } = context.strokeStyle
    context.globalCompositeOperation = 'destination-out'
    context.lineWidth = 40
    context.lineCap = 'square'
    dispatch('drawLine', remote)
    context.globalCompositeOperation = 'source-over'
    context.strokeStyle = strokeStyle
    context.lineCap = 'round'
    commit('configureLineWidth')
  },
  imagePaste: function({ getters, commit }, image) {
    const { context } = getters
    const maxWidth = WHITEBOARD_BASE_SIZE.width
    const maxHeight = WHITEBOARD_BASE_SIZE.height
    let drawWidth, drawHeight

    if (image.width > image.height) {
      let ratio = image.height / image.width
      drawWidth = maxWidth
      drawHeight = maxWidth * ratio
      if (drawHeight > maxHeight) {
        ratio = image.width / image.height
        drawWidth = maxHeight * ratio
        drawHeight = maxHeight
      }
    } else if (image.height > image.width) {
      let ratio = image.width / image.height
      drawWidth = maxHeight * ratio
      drawHeight = maxHeight
      if (drawWidth > maxWidth) {
        ratio = image.height / image.width
        drawWidth = maxWidth
        drawHeight = maxWidth * ratio
      }
    } else {
      drawWidth = maxHeight
      drawHeight = maxHeight
    }
    context.save()
    context.drawImage(image, 0, 0, image.width, image.height, 0, 0, drawWidth, drawHeight)
    context.restore()
    commit('setSendImageData')
  },
  snapshot: function({ commit }, payload) {
    const { whiteboardCanvas, projector } = payload

    const snapshotCanvas = document.createElement('canvas')
    snapshotCanvas.width = WHITEBOARD_BASE_SIZE.width
    snapshotCanvas.height = WHITEBOARD_BASE_SIZE.height
    const snapshotContext = snapshotCanvas.getContext('2d')
    const tmpStyle = snapshotContext.fillStyle
    snapshotContext.fillStyle = 'rgba(0, 0, 0, 0.6)'
    snapshotContext.beginPath()
    snapshotContext.fillRect(0, 0, Math.abs(-WHITEBOARD_BASE_SIZE.width), Math.abs(-WHITEBOARD_BASE_SIZE.height))
    snapshotContext.closePath()
    snapshotContext.fillStyle = tmpStyle

    if (projector) {
      const videoWidth = projector.videoWidth
      const videoHeight = projector.videoHeight
      const mx = WHITEBOARD_BASE_SIZE.width / videoWidth
      const my = WHITEBOARD_BASE_SIZE.height / videoHeight
      const rw = mx < my ? videoWidth * mx : videoWidth * my
      const rh = mx < my ? videoHeight * mx : videoHeight * my
      const x = (WHITEBOARD_BASE_SIZE.width  - rw) / 2
      const y = (WHITEBOARD_BASE_SIZE.height - rh) / 2
      snapshotContext.drawImage(projector, 0, 0, videoWidth, videoHeight, x, y, rw, rh)
    }

    snapshotContext.drawImage(whiteboardCanvas, 0, 0)
    commit('setSnapshotImage', snapshotCanvas.toDataURL('image/png'))
  },
  clear: function({ getters }) {
    const { context } = getters
    context.clearRect(0, 0, WHITEBOARD_BASE_SIZE.width, WHITEBOARD_BASE_SIZE.height)
  },
  receiveImageData: function({ getters }, data) {
    const { context } = getters
    const imageData = context.createImageData(WHITEBOARD_BASE_SIZE.width, WHITEBOARD_BASE_SIZE.height)
    const pixels = imageData.data
    const buffer = new Uint8Array(data)
    for (var i = 0; i < pixels.length; i++) {
      pixels[i] = buffer[i]
    }
    context.putImageData(imageData, 0, 0)
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
